import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

export default function Dashboard() {
  const [stats, setStats] = useState({
  totalDefects: 0,
  activeDefects: 0,
  abandonedInspections: 0,
  inactiveVehicles: 0
});
  const [recentDefects, setRecentDefects] = useState<any[]>([]);
  const [abandonedInspections, setAbandonedInspections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [defectsRes, inspectionsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API_BASE}/defects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/inspections`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const defects = defectsRes.data.data;
      const inspections = inspectionsRes.data.data;
      const vehicles = vehiclesRes.data.data;

      const ABANDONED_AFTER_MS = 2 * 60 * 60 * 1000;
      const now = Date.now();
      const isStaleInProgress = (i: any) =>
        i.status === 'in_progress' &&
        i.start_time &&
        now - new Date(i.start_time).getTime() > ABANDONED_AFTER_MS;

      const abandoned = inspections.filter((i: any) => i.status === 'abandoned');
      const staleInProgress = inspections.filter(isStaleInProgress);
      const combinedAbandoned = [...abandoned, ...staleInProgress].sort(
        (a: any, b: any) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
      );

      const unresolvedDefects = defects.filter((d: any) => d.status !== 'resolved');
      const inactiveVehicles = vehicles.filter((v: any) => v.status && v.status !== 'active');
      
      setStats({
        totalDefects: unresolvedDefects.length,
        activeDefects: defects.filter((d: any) => d.status === 'in_progress' || d.status === 'acknowledged').length,
        abandonedInspections: combinedAbandoned.length,
        inactiveVehicles: inactiveVehicles.length
      });

      // Show only unresolved defects in recent list
      setRecentDefects(unresolvedDefects.slice(0, 5));

      // Show latest abandoned/stale inspections
      setAbandonedInspections(combinedAbandoned.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAbandoned = async (inspectionId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/inspections/mark-abandoned`,
        { inspection_id: inspectionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadDashboardData();
    } catch (error) {
      console.error('Error marking inspection abandoned:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mb-4"></div>
        <p className="text-gray-600">Loading dashboard...</p>
        <p className="text-sm text-gray-400 mt-2">This may take a moment on first load</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Open Defects" value={stats?.totalDefects || 0} icon={<AlertTriangleIcon className="w-6 h-6" />} color="bg-red-500" />
        <StatCard title="In Progress" value={stats?.activeDefects || 0} icon={<ToolsIcon className="w-6 h-6" />} color="bg-orange-500" />
        <StatCard title="Abandoned Inspections" value={stats?.abandonedInspections || 0} icon={<ClockIcon className="w-6 h-6" />} color="bg-gray-600" />
        <StatCard title="Inactive Vehicles" value={stats?.inactiveVehicles || 0} icon={<TruckIcon className="w-6 h-6" />} color="bg-gray-500" />
      </div>

      {/* Abandoned Inspections */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Abandoned Inspections</h2>
        <div className="space-y-4">
          {abandonedInspections.length === 0 ? (
            <p className="text-gray-500">No abandoned inspections</p>
          ) : (
            abandonedInspections.map((inspection) => {
              const isStale = inspection.status === 'in_progress';
              return (
                <div key={inspection.id} className="border-l-4 border-gray-500 pl-4 py-2">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="font-medium text-gray-800">
                        {inspection.vehicle_registration || 'Vehicle not available'}
                      </p>
                      <p className="text-sm text-gray-600">Driver: {inspection.driver_name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Started {new Date(inspection.start_time).toLocaleDateString()} • {new Date(inspection.start_time).toLocaleTimeString()}
                        {isStale ? ' • Stale (in progress)' : ' • Abandoned'}
                      </p>
                    </div>

                    {isStale ? (
                      <button
                        onClick={() => markAbandoned(inspection.id)}
                        className="px-3 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                      >
                        Mark abandoned
                      </button>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        abandoned
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Recent Defects */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Defects</h2>
        <div className="space-y-4">
          {recentDefects.length === 0 ? (
            <p className="text-gray-500">No defects reported</p>
          ) : (
            recentDefects.map((defect) => (
              <div key={defect.id} className="border-l-4 border-red-500 pl-4 py-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">{defect.registration_number}</p>
                    <p className="text-sm text-gray-600">{defect.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Reported by {defect.driver_name} • {new Date(defect.reported_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    defect.severity === 'critical' ? 'bg-red-100 text-red-800' :
                    defect.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {defect.severity}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }: { title: string; value: number; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
        </div>
        <div className={`${color} w-12 h-12 rounded-lg flex items-center justify-center text-white`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function ToolsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6a2 2 0 0 0 2.8 2.8l6-6a4 4 0 0 0 5.4-5.4l-3 3-2.8-2.8 3-3Z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 22a10 10 0 1 0-10-10 10 10 0 0 0 10 10Z" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function TruckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10 17h4" />
      <path d="M3 17V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v11" />
      <path d="M17 8h4l2 3v6h-6" />
      <path d="M7 17a2 2 0 1 0 4 0" />
      <path d="M17 17a2 2 0 1 0 4 0" />
    </svg>
  );
}
